/**
 * _app — Next.js Pages Router app shell.
 *
 * Derleme mimarisi (2026-05-16 best-practice fix):
 *   - Top-level statik import minimum: dayjs, useRouter, Toaster, axios setup
 *   - Provider chain → AppProviders dynamic({ssr:true}) (lazy chunk)
 *   - Layout + AppShellProviders + Public-only dynamic'ler korunur
 *   - /_error / /404 / /500 early-return → AppProviders YÜKLENMEZ → compile graph minimum
 *
 * Beklenen etki: /_error compile 4316 modül → ~150-300 modül (×15-20 hız).
 *
 * Mimari karar:
 *   Pages Router'da _app.js shared bundle. Statik import'lar her sayfa
 *   (dahil _error) build-time tree-shake EDİLEMEZ. Bu yüzden tüm provider'ları
 *   tek dynamic chunk'a topladık. Next.js App Router'a geçince doğal route-level
 *   layout'larla bu mimari otomatik olur, şimdilik manuel.
 */

import '@/styles/globals.css';

// Side-effect axios setup (top-level, axios her sayfada gerekli)
import '@/shared/utils/axiosSetup';

import { useRouter } from 'next/router';
import { Toaster } from 'react-hot-toast';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type { ReactNode, ReactElement, ComponentType } from 'react';
import { trackCentralPublicPageView } from '@/application/services/publicPageViewTracking';
import Script from 'next/script';
import Head from 'next/head';
import { isAppRoute } from '@/application/auth/routePolicy';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

import { initErrorLogger } from '@/shared/utils/errorLogger';
import { captureAttribution } from '@/shared/utils/attribution';
import api from '@/shared/utils/axiosConfig';
import { getAppPlatform } from '@/shared/analytics/appPlatform';

// ============================================================================
// LAZY CHUNK'LAR — _error/404/500 early-return ile compile edilmez
// ============================================================================

// Tüm app-level provider chain (Auth + AuthGate + Firma + I18n + ErrorBoundary + MaintenanceGate)
const AppProviders = dynamic(
  () => import('@/presentation/components/providers/AppProviders'),
  { ssr: true }
);

// Public sayfa shell wrapper'ı (MUI ThemeProvider + AntD)
const AppShellProviders = dynamic(
  () => import('@/presentation/components/providers/AppShellProviders'),
  { ssr: true }
);

// In-app sayfalar layout zinciri (UserLayout + Sidebar + SssDrawer + ProblemsPanel)
const Layout = dynamic(
  () => import('@/presentation/components/layout').then((m) => ({ default: m.Layout })),
  { ssr: true }
);

// Client-only side widgets
const CookieConsent = dynamic(() => import('@/presentation/components/CookieConsent'), { ssr: false });
// SW otomatik güncelleme — user deploy'u elle cache temizlemeden alır (null render, effect-only)
const ServiceWorkerUpdater = dynamic(() => import('@/presentation/components/ServiceWorkerUpdater'), { ssr: false });
const AnalyticsInternalMarker = dynamic(
  () => import('@/presentation/components/analytics/AnalyticsInternalMarker'),
  { ssr: false }
);

// ============================================================================
// CONFIG
// ============================================================================

dayjs.locale('tr');

const TOAST_CONFIG: React.ComponentProps<typeof Toaster> = {
  position: 'top-center',
  containerStyle: { zIndex: 99999, top: 16 },
  gutter: 10,
  toastOptions: {
    duration: 4500,
    style: {
      background: '#ffffff',
      color: '#0f172a',
      border: '1px solid #cbd5e1',
      borderRadius: '10px',
      boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.18), 0 8px 16px -6px rgba(15, 23, 42, 0.10)',
      fontSize: '13px',
      fontWeight: 500,
      padding: '12px 18px',
      maxWidth: '520px',
      minWidth: '320px',
      letterSpacing: '0.1px',
    },
    success: {
      iconTheme: { primary: '#10b981', secondary: '#ecfdf5' },
      style: { borderLeft: '5px solid #10b981' },
    },
    error: {
      iconTheme: { primary: '#ef4444', secondary: '#fef2f2' },
      style: { borderLeft: '5px solid #ef4444' },
      duration: 6000,
    },
    loading: {
      iconTheme: { primary: '#64748b', secondary: '#f1f5f9' },
      style: { borderLeft: '5px solid #64748b' },
    },
  },
};

const authPages: string[] = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/auth/login',
  '/auth/register',
  '/auth/reset-password',
  '/auth/update-password',
  '/auth/callback',
  '/auth/onaylandi',
];

const PUBLIC_LANDING_PATHS: string[] = [
  '/',
  '/ozellikler',
  '/isg-yazilimi',
  '/iso-45001-yazilimi',
  '/ai-destekli-risk-analizi',
  '/fotograftan-tehlike-tespiti',
  '/ekipman-periyodik-kontrol-yazilimi',
  '/acil-durum-plani-yazilimi',
  '/yillik-calisma-plani-yazilimi',
  '/yillik-egitim-plani-yazilimi',
  '/isg-egitim-sunumu-yazilimi',
  '/uygunsuzluk-yonetimi-yazilimi',
  '/is-kazasi-bildirim-yazilimi',
  '/isg-egitim-kayitlari-yazilimi',
  '/isg-kurulu-yazilimi',
  '/calisan-yonetimi-yazilimi',
  '/calisan-temsilcisi-secimi-yazilimi',
  '/taseron-evrak-takip-yazilimi',
  '/is-izni-yazilimi',
  '/onlem-takip-yazilimi',
  '/acil-durum-tatbikati-yazilimi',
  '/kacis-plani-yazilimi',
  '/onayli-defter-yazilimi',
  '/yonetim-gozden-gecirme-yazilimi',
  '/calisan-katilimi-proseduru-yazilimi',
  '/baglam-analizi-yazilimi',
  '/kapsam-beyani-yazilimi',
  '/isg-politikasi-yazilimi',
  '/roller-yetki-matrisi-yazilimi',
  '/liderlik-ve-taahhut-yazilimi',
  '/iletisim-plani-yazilimi',
  '/degisiklik-yonetimi-yazilimi',
  '/yuklenici-yonetimi-yazilimi',
  '/ic-denetim-yazilimi',
  '/uyum-degerlendirme-yazilimi',
  '/yetkinlik-matrisi-yazilimi',
  '/dokuman-merkezi-yazilimi',
  '/surekli-iyilestirme-yazilimi',
  '/ai-vision',
  '/ai-mevzuat',
  '/vr-acil-durum-tatbikati',
  '/ai-gozluk-risk-analizi',
  '/ai-gozluk-saha-gozlem',
  '/dijital-atolye',
  '/tek-uzman-icin',
  '/ofismux',
  '/isg-katip-eklentisi',
  '/fiyatlandirma',
  '/fiyatlandirma/karsilastir',
  '/iletisim',
  '/hakkimizda',
  '/kvkk-aydinlatma-metni',
  '/gizlilik-politikasi',
  '/kullanim-kosullari',
  '/cerez-politikasi',
  '/hakkinda',
  '/sorumluluk-reddi',
  '/mesafeli-satis-sozlesmesi',
  '/teslimat-iade-sartlari',
  '/guvenlik',
  '/ornekler',
  '/kayit-talep-alindi',
  '/account-required',
  '/account-suspended',
];

// ============================================================================
// TYPES
// ============================================================================

type PageComponent = ComponentType<Record<string, unknown>> & {
  getLayout?: (page: ReactElement) => ReactElement;
  layout?: ComponentType | null;
  customLayout?: boolean;
  userLayoutProps?: Record<string, unknown>;
};

type NextRouter = ReturnType<typeof useRouter>;

interface AppProps {
  Component: PageComponent;
  pageProps: Record<string, unknown>;
}

interface MainAppProps {
  Component: PageComponent;
  pageProps: Record<string, unknown>;
  router: NextRouter;
}

interface PassThroughWrapperProps {
  children: ReactNode;
}

interface CookieConsentState {
  analytics?: boolean;
}

type GtagFn = (...args: unknown[]) => void;

type GtagWindow = Window & {
  __riskmatikGtagInit?: boolean;
  dataLayer?: unknown[];
  gtag?: GtagFn;
};

// ============================================================================
// APP
// ============================================================================

function PassThroughWrapper({ children }: PassThroughWrapperProps) {
  return <>{children}</>;
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  // /_error / /404 / /500 — bare Component, provider chain bypass.
  // Bu early-return webpack'in AppProviders/AppShellProviders/Layout dynamic
  // chunk'larını _error bundle'ına dahil etmemesini sağlar (compile süresi fix).
  const isErrorPage =
    router.pathname === '/_error' ||
    router.pathname === '/404' ||
    router.pathname === '/500';
  if (isErrorPage) {
    return <Component {...pageProps} />;
  }

  return <MainApp Component={Component} pageProps={pageProps} router={router} />;
}

/**
 * MainApp — Normal sayfa akışı (error sayfaları DIŞINDA).
 * Hook'lar burada çağrılır; isErrorPage early-return Hook Rules ihlali olmaz.
 */
function MainApp({ Component, pageProps, router }: MainAppProps) {
  const [cookieConsent, setCookieConsent] = useState<CookieConsentState | null>(null);
  // analyticsLoaded state KALDIRILDI — loop kaynağıydı (CLAUDE.md ALTIN KURAL #4).
  // gtag yükleme artık useEffect'te idempotent guard ile yapılır (aşağıda).
  const [isMaintenanceActive, setIsMaintenanceActive] = useState<boolean>(false);

  // Maintenance flag — Toaster/CookieConsent banner gizleme için
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getPublicConfig } = await import('@/infrastructure/api/configService');
        const { maintenanceMode } = await getPublicConfig();
        if (!cancelled) setIsMaintenanceActive(Boolean(maintenanceMode));
      } catch {
        /* sessiz */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [router.pathname]);

  // App/oturum telemetrisi — oturum başına 1 kez platform ping (mobil/app kullanım metriği).
  // Tüm platformlar loglanır (app/web karşılaştırması); hacim = 1/oturum. Fire-and-forget.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('rm_oturum_ping')) return;
      sessionStorage.setItem('rm_oturum_ping', '1');
    } catch {
      return; // sessionStorage erişilemezse sessizce çık
    }
    api.post('/kullanim/oturum', { platform: getAppPlatform() }).catch(() => {
      /* telemetri — ana akışı bozmaz */
    });
  }, []);

  // Çerez onayı + global error logger
  useEffect(() => {
    initErrorLogger();
    // Trafik kaynağı first-touch capture — idempotent (zaten varsa overwrite yok), tek sefer
    captureAttribution();

    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      try {
        const parsed: CookieConsentState = JSON.parse(consent);
        setCookieConsent(parsed);
      } catch {
        /* parse error - ignore */
      }
    }

    const handleConsentUpdate = () => {
      const updated = localStorage.getItem('cookie-consent');
      if (updated) {
        try {
          const parsed: CookieConsentState = JSON.parse(updated);
          setCookieConsent(parsed);
        } catch {
          /* parse error - ignore */
        }
      }
    };

    window.addEventListener('cookieConsentUpdate', handleConsentUpdate);
    return () => window.removeEventListener('cookieConsentUpdate', handleConsentUpdate);
  }, []);

  // Google Analytics — Consent Mode v2 (Ali 2026-05-22, v2 yeniden tasarım)
  // İlk denemede (commit 6cd8cd3cd) <Script> JSX gate + state set loop yarattı.
  // Bu sürüm: idempotent window guard + DOM-level script inject + JSX render YOK.
  // Sonuç: hydration mismatch riski 0, re-render tetikleyici 0, ALTIN KURAL #4 uyumlu.
  useEffect(() => {
    const w: GtagWindow = window;
    const host = w.location.hostname;
    const isProduction = host === 'www.riskmatik.com' || host === 'riskmatik.com';
    if (!isProduction) return;
    if (w.__riskmatikGtagInit) return;
    w.__riskmatikGtagInit = true;

    // dataLayer + gtag global
    w.dataLayer = w.dataLayer || [];
    const dataLayer = w.dataLayer;
    // gtag.js yalnız `arguments` nesnesini komut sayar; yayma (...args) her ziyareti kaybettirir
    // (26 Eki incident, guard scripts/check-gtag-arguments.mjs).
    const gtag: GtagFn = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments);
    };
    w.gtag = gtag;

    // Consent Mode v2 default DENIED (KVKK güvenli başlangıç)
    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500,
    });
    gtag('js', new Date());
    gtag('config', 'G-82JBB66F2Y', {
      anonymize_ip: true,
      send_page_view: true,
    });
    gtag('config', 'AW-11469021640');

    // Script tag inject (next/script bypass — JSX render edilmiyor). Sayfa load olduktan sonra, tarayıcı boşta
    // kalınca (next/script lazyOnload eşdeğeri; PageSpeed 21 Eki: gtag + Ads 354 KiB, 487 ms CPU LCP ile yarışıyordu).
    // Olay kaybı yok: yukarıdaki gtag() çağrıları ve sonraki page_view / purchase / dönüşüm olayları dataLayer
    // kuyruğunda bekler, gtag.js yüklenince işler.
    const inject = () => {
      const script = document.createElement('script');
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-82JBB66F2Y';
      script.async = true;
      document.head.appendChild(script);
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(inject, { timeout: 4000 });
      else setTimeout(inject, 2000);
    };
    if (document.readyState === 'complete') schedule();
    else w.addEventListener('load', schedule, { once: true });
  }, []);

  // Consent update — kullanıcı banner'dan "Kabul" basarsa granted
  useEffect(() => {
    const w: GtagWindow = window;
    if (cookieConsent?.analytics && w.gtag) {
      w.gtag('consent', 'update', { analytics_storage: 'granted' });
    }
  }, [cookieConsent?.analytics]);

  // SPA route change → page_view tetikle (Ali 2026-05-28: GSC click 416 / GA
  // Organic 29 tutarsızlığının kökü — gtag config sadece ilk yüklemede page_view
  // gönderiyor, Next.js client-side navigasyonlarda manuel tetiklemek şart).
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const w: GtagWindow = window;
      if (typeof window === 'undefined' || !w.gtag) return;
      w.gtag('event', 'page_view', {
        page_path: url,
        page_location: window.location.origin + url,
        page_title: document.title,
      });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events]);

  // Merkezi public GORUNTULEME — self-track etmeyen tüm public sayfalar (anasayfa,
  // SEO landing, hub index, blog, yasal) için attribution. GA effect'ten AYRI:
  // gtag-guard YOK → ad-blocker'da bile backend PublicEtkilesim'e gider (landing yedeği).
  // Dedup + gate helper'da (publicPageViewTracking SSoT). Loop-safe: deps [router.events].
  useEffect(() => {
    trackCentralPublicPageView(router.asPath); // ilk yükleme
    const onChange = (url: string) => trackCentralPublicPageView(url);
    router.events.on('routeChangeComplete', onChange);
    return () => router.events.off('routeChangeComplete', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);

  // Rota türü
  const isAdminPage = router.pathname.startsWith('/admin');
  const isAuthPage =
    authPages.includes(router.pathname) ||
    authPages.some((route) => router.pathname.startsWith(route + '/'));

  // Next.js getLayout pattern
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);
  const PageLayoutComponent = Component.layout;

  // Public landing/araç/blog/auth sayfaları MUI+AntD shell yüklemez (~300-400KB tasarruf)
  const isPublicLanding =
    PUBLIC_LANDING_PATHS.includes(router.pathname) ||
    router.pathname.startsWith('/araclar') ||
    router.pathname.startsWith('/blog') ||
    router.pathname.startsWith('/risk-analizi/ornekler') ||
    router.pathname.startsWith('/saha-gozlem/ornekler') ||
    router.pathname.startsWith('/yillik-calisma-plani/ornekler') ||
    router.pathname.startsWith('/yillik-egitim-plani/ornekler') ||
    router.pathname.startsWith('/egitim-sunum/ornekler') ||
    router.pathname.startsWith('/acil-durum-plani/ornekler') ||
    router.pathname.startsWith('/tatbikat-plani/ornekler') ||
    router.pathname.startsWith('/formlar/ornekler') ||
    router.pathname.startsWith('/talimatlar/ornekler') ||
    router.pathname === '/e' ||
    router.pathname.startsWith('/e/') ||
    router.pathname === '/t' ||
    router.pathname.startsWith('/t/') ||       // taşeron evrak portalı (hesapsız) — app shell yüklemez
    router.pathname.startsWith('/izin/') ||      // iş izni karekod saha durumu (/izin/[token]) — app shell yüklemez
    router.pathname.startsWith('/ekipman/') ||   // ekipman karekod public raporu (/ekipman/[token]) — app shell yüklemez
    isAuthPage;
  const ShellWrapper = isPublicLanding ? PassThroughWrapper : AppShellProviders;

  // Giriş gerektiren uygulama sayfaları arama motoruna kapalı (SSoT: routePolicy). Anonim istekte
  // başlıksız spinner render edip 200 döndükleri için Google ince/soft-404 sayfa olarak indeksliyordu.
  const robotsNoindex = isAppRoute(router.pathname) ? (
    <meta name="robots" content="noindex,nofollow" key="robots" />
  ) : null;

  // Viewport — interactive-widget=resizes-content: Android'de yazılım klavyesi açılınca layout
  // küçülür, footer/submit + aktif input klavyenin altında kalmaz (mobil-first, 2026-06-08 fix).
  // next/head'de olmalı (key=viewport): _document'ta Next'in varsayılan viewport'uyla çift basılıyordu.
  const appHead = (
    <Head>
      <meta
        name="viewport"
        key="viewport"
        content="width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content"
      />
      {robotsNoindex}
    </Head>
  );

  // Maintenance bypass: admin + auth + landing
  const bypassMaintenance = isAdminPage || isAuthPage || router.pathname === '/';

  // Cookie consent TÜM public sayfalarda gösterilir (anasayfa + blog + örnekler hub'ları +
  // araçlar + auth). Kök sebep (2026-06-11): banner yalnız '/'de çıkıyordu → Google'dan
  // doğrudan /blog veya /risk-analizi/ornekler'e düşen SEO ziyaretçisi banner'ı HİÇ görmüyor
  // → consent 'denied' kalıyor → GA cookieless/undercount (GSC 562 tık / GA ~37 kullanıcı uçurumu).
  // CookieConsent component'i localStorage('cookie-consent') guard'lı — karar verilince
  // tekrar görünmez, o yüzden tüm public sayfalarda render güvenli (spam yok).
  const showCookieConsent = isPublicLanding || router.pathname === '/';

  // Custom layout (admin / auth / getLayout / layout=null)
  // 🔴 <Layout>-skip = SADECE `.layout=null` (veya customLayout/getLayout). Kendi PublicHeader'ını
  // basan HER landing `.layout=null` koyar (guard ZORUNLU kılar) → Layout atlanır → çift header YOK.
  // `.layout=null` KOYMAYAN public sayfalar (hakkimizda/iletisim/ozellikler/isg-yazilimi/yasal vb.)
  // header'ı in-app <Layout>'tan alır → HEADERSIZ kalmaz.
  // ⚠️ 2026-07-31 REGRESYON DERSİ: koşula `isPublicLanding` eklemek, header'ı Layout'tan alan 13 public
  // sayfayı (SEO pillar'ları dahil) HEADERSIZ bıraktı. Çift-header koruması artık PUBLIC_LANDING_PATHS
  // ÜYELİĞİ değil, guard'ın "PublicHeader render eden sayfa .layout=null taşımalı" şartı. isPublicLanding
  // SADECE bundle-skip'te (satır 334, ShellWrapper) kullanılır — Layout-skip'te ASLA.
  if (isAdminPage || isAuthPage || PageLayoutComponent === null || Component.getLayout) {
    return (
      <div>
        {appHead}
        <ServiceWorkerUpdater />
        <AppProviders bypassMaintenance={bypassMaintenance}>
          <ShellWrapper>
            {Component.getLayout ? getLayout(<Component {...pageProps} />) : <Component {...pageProps} />}
            {!isMaintenanceActive && (
              <>
                <Toaster {...TOAST_CONFIG} />
                {showCookieConsent && <CookieConsent />}
              </>
            )}
            <AnalyticsInternalMarker />
          </ShellWrapper>
        </AppProviders>
      </div>
    );
  }

  // Standart sayfa (Layout zinciri)
  return (
    <div>
      {appHead}
      <ServiceWorkerUpdater />
      <AppProviders bypassMaintenance={bypassMaintenance}>
        <ShellWrapper>
          {Component.getLayout ? (
            getLayout(<Component {...pageProps} />)
          ) : Component.customLayout ? (
            <Component {...pageProps} />
          ) : (
            <Layout templateComponent={PageLayoutComponent} userLayoutProps={Component.userLayoutProps || {}}>
              <Component {...pageProps} />
            </Layout>
          )}
          {!isMaintenanceActive && (
            <>
              <Toaster {...TOAST_CONFIG} />
              {showCookieConsent && <CookieConsent />}
            </>
          )}
          <AnalyticsInternalMarker />
        </ShellWrapper>
      </AppProviders>

      {/* gtag yükleme yukarıdaki useEffect içinde idempotent DOM-inject ile
          yapılır (loop önleme — CLAUDE.md ALTIN KURAL #4). JSX render YOK. */}
    </div>
  );
}
